import { Link } from "@remix-run/react";
import { useEffect } from "react";
import { ArrowRight } from "lucide-react";
import { useRegionConfig } from "~/store/regionConfigStore";
import { useNavStore } from "~/store/navStore";
import {
  aboutOfficeHubNavLinks,
  becomePartnerNavLinks,
  industryLinks,
  NAV_DROPDOWN_CARD_CLASS,
  NAV_DROPDOWN_MAIN_PANEL_CLASS,
  NAV_DROPDOWN_PROMO_PANEL_CLASS,
} from "../constant";
import { getRegionUrlPrefixWithCountryCode } from "~/lib/helpers/slugHelper";
import { Card } from "~/components/ui/card";
import { NAV_ICON_BOX_CLASS } from "../materialNavIcon";
import { ResourceNavIcon } from "./resourceNavIcons";

const columnLabelClass =
  "text-xs font-semibold uppercase tracking-[0.08em] text-oh-grey-heavy";

function getIndustryLinkUrl(
  link: (typeof industryLinks)[number],
  regionPrefix: string,
) {
  if (link.linkType === "page") {
    return `${regionPrefix}/${link.path}`.replace(/\/+/g, "/");
  }

  return link.path
    ? `${regionPrefix}/blog/${link.path}`
    : `${regionPrefix}/blog`;
}

export default function Resources() {
  const { regionConfig } = useRegionConfig();
  const { blogData, blogLoading, fetchBlogData } = useNavStore();

  const regionPrefix = getRegionUrlPrefixWithCountryCode(regionConfig?.region);
  const featuredPost = blogData?.blogs?.[0];

  useEffect(() => {
    if (regionConfig.country) {
      fetchBlogData(regionConfig.country);
    }
  }, [fetchBlogData, regionConfig.country]);

  const sanitizedContent = featuredPost?.blogContent
    ?.replace(/<[^>]*>/g, " ")
    ?.replace(/\s+/g, " ")
    ?.trim();

  return (
    <Card className={NAV_DROPDOWN_CARD_CLASS}>
      <div
        className={`${NAV_DROPDOWN_MAIN_PANEL_CLASS} w-full p-6 lg:w-[780px] lg:flex-none lg:p-8`}
      >
        <div className='grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3'>
          <div className='w-full min-w-0 lg:w-[220px]'>
            <span className={columnLabelClass}>Learn The Industry</span>
            <div className='mt-4 flex flex-col gap-1'>
              {industryLinks.map((link) => (
                <Link
                  key={link.label}
                  data-spinner='true'
                  to={getIndustryLinkUrl(link, regionPrefix)}
                  prefetch='intent'
                  className='group flex items-start gap-3 rounded-md p-2 transition-colors hover:bg-gray-100'
                >
                  <ResourceNavIcon
                    icon={link.icon}
                    boxClassName={`mt-0.5 ${NAV_ICON_BOX_CLASS}`}
                  />
                  <div className='min-w-0 space-y-0.5'>
                    <span className='text-base font-semibold leading-6 text-black transition-colors'>
                      {link.label}
                    </span>
                    <p className='text-sm leading-6 text-oh-gray'>
                      {link.subtitle}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          <div className='w-full min-w-0 lg:w-[220px]'>
            <span className={columnLabelClass}>About Office Hub</span>
            <div className='mt-4 flex flex-col gap-1'>
              {aboutOfficeHubNavLinks.map((item) => (
                <Link
                  key={item.path}
                  data-spinner='true'
                  to={`${regionPrefix}/${item.path}`}
                  prefetch='intent'
                  className='group flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-gray-100'
                >
                  <ResourceNavIcon icon={item.icon} />
                  <span className='text-base font-semibold leading-6 text-black transition-colors'>
                    {item.label}
                  </span>
                </Link>
              ))}
            </div>
          </div>

          <div className='w-full min-w-0 lg:w-[220px]'>
            <span className={columnLabelClass}>Become a Partner</span>
            <div className='mt-4 flex flex-col gap-1'>
              {becomePartnerNavLinks.map((item) => {
                if (item.external) {
                  return (
                    <a
                      key={item.label}
                      href={item.path}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='group flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-gray-100'
                    >
                      <ResourceNavIcon icon={item.icon} />
                      <span className='text-base font-semibold leading-6 text-black transition-colors'>
                        {item.label}
                      </span>
                    </a>
                  );
                }

                return (
                  <Link
                    key={item.label}
                    data-spinner='true'
                    to={`${regionPrefix}/${item.path}`.replace(/\/+/g, "/")}
                    prefetch='intent'
                    className='group flex items-center gap-3 rounded-md p-2 transition-colors hover:bg-gray-100'
                  >
                    <ResourceNavIcon icon={item.icon} />
                    <span className='text-base font-semibold leading-6 text-black transition-colors'>
                      {item.label}
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className={NAV_DROPDOWN_PROMO_PANEL_CLASS}>
        <span className='text-xs font-semibold uppercase tracking-[0.08em] text-oh-primary'>
          Featured Post
        </span>

        {blogLoading ? (
          <>
            <div className='mt-2 h-14 w-full animate-pulse rounded bg-gray-200' />
            <div className='mt-4 h-44 w-full animate-pulse rounded-lg bg-gray-200' />
            <div className='mt-4 space-y-2'>
              <div className='h-3 w-full animate-pulse rounded bg-gray-200' />
              <div className='h-3 w-4/5 animate-pulse rounded bg-gray-200' />
            </div>
            <div className='mt-5 h-12 w-full animate-pulse rounded-md bg-gray-200' />
          </>
        ) : featuredPost ? (
          <>
            <span className='mt-2 text-lg font-bold leading-7 text-black'>
              {featuredPost.title}
            </span>

            <img
              src={`https://ik.imagekit.io/officehub/tr:h-600,f-avif/${featuredPost.bannerImageUrl}`}
              alt={featuredPost.title}
              className='mt-4 h-44 w-full rounded-lg object-cover'
              loading='lazy'
            />

            <p className='mt-4 line-clamp-3 text-sm leading-7 text-oh-dark-gray'>
              {sanitizedContent}
            </p>

            <Link
              data-spinner='true'
              to={`${regionPrefix}/blog/${featuredPost.slug}`}
              prefetch='intent'
              className='mt-auto flex h-12 w-full shrink-0 items-center justify-center gap-2 rounded-md bg-oh-primary text-sm font-semibold text-white transition-colors hover:bg-oh-dullTeal'
            >
              Read article
              <ArrowRight size={18} />
            </Link>
          </>
        ) : null}
      </div>
    </Card>
  );
}
